import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import StandardNavbar from '../components/StandardNavbar';
import Footer from '../components/Footer';

import contactBackground from '../assets/headerContact.webp';

function Contacto() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    consulta: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar mensajes de error al escribir
    if (submitStatus) {
      setSubmitStatus('');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');
    setErrorMessage('');
    
    // ✅ VALIDACIÓN EN FRONTEND
    if (!formData.nombre.trim() || !formData.email.trim() || 
        !formData.telefono.trim() || !formData.consulta || !formData.mensaje.trim()) {
      setSubmitStatus('error');
      setErrorMessage('Por favor completa todos los campos');
      setIsSubmitting(false);
      return;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus('error');
      setErrorMessage('Por favor ingresa un email válido');
      setIsSubmitting(false);
      return;
    }

    try {
      console.log('📤 Enviando consulta...', formData);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/contacto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('📥 Respuesta del servidor:', response.status);
      
      const data = await response.json();
      console.log('📋 Datos de respuesta:', data);

      if (response.ok && data.success) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          consulta: '',
          mensaje: ''
        });
        
        // Scroll hacia arriba para mostrar el mensaje
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Ocultar mensaje de éxito después de 5 segundos
        setTimeout(() => {
          setSubmitStatus('');
        }, 5000);
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.message || 'Error al enviar el mensaje. Por favor, intenta nuevamente.');
      }
    } catch (error) {
      console.error('❌ Error en handleSubmit:', error);
      setSubmitStatus('error');
      setErrorMessage('Error de conexión. Verifica tu conexión a internet e intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contacto | Noche y Día Turismo</title>
        <meta name="description" content="Contáctanos y sé parte de un viaje inolvidable" />
      </Helmet>
      <StandardNavbar />

      <header className="relative text-gray-800 text-center">
        {/* Contenedor de la imagen con altura responsive */}
        <div className="relative h-[200px] md:h-[300px] lg:h-[400px] overflow-hidden">
          <img 
            src={contactBackground} 
            alt="Contacto Background" 
            className="absolute inset-0 w-full h-full object-cover" 
          />

          {/* Gradiente inferior para unificar con el contenido */}
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white via-white/5 to-transparent"></div>

          {/* Contenido del texto alineado a la izquierda */}
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-8 md:px-16 lg:px-24">
              <div className="max-w-2xl text-left">
                <p className="text-sm md:text-base text-gray-600 mb-3 font-medium">
                  A veces una aventura empieza con un mensaje.
                </p>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-montserrat mb-2">
                  Contáctanos y se parte de un
                </h1>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-montserrat text-[#4A5FE8]">
                  viaje inolvidable
                </h2>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <div className="relative pt-16 min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Formulario */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-[#1E3A5F] mb-6 text-center">
                Te Responderemos A La Brevedad
              </h3>
              
              {/* ✅ MENSAJE DE ÉXITO MEJORADO */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-l-4 border-green-500 text-green-800 px-4 py-4 rounded mb-6 shadow-sm">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">¡Mensaje enviado exitosamente!</p>
                      <p className="text-sm mt-1">Te responderemos a la brevedad.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* ✅ MENSAJE DE ERROR MEJORADO */}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-800 px-4 py-4 rounded mb-6 shadow-sm">
                  <div className="flex items-start">
                    <svg className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">Error al enviar el mensaje</p>
                      <p className="text-sm mt-1">{errorMessage || 'Por favor, intenta nuevamente.'}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-all"
                      placeholder="Tu nombre"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-all"
                      placeholder="tu@email.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-all"
                      placeholder="Ej: 3515215941"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de consulta <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="consulta"
                      value={formData.consulta}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-all"
                      required
                    >
                      <option value="">Selecciona una opción</option>
                      <option value="paquetes">Consulta sobre paquetes</option>
                      <option value="reservas">Reservas</option>
                      <option value="modificaciones">Modificaciones de viaje</option>
                      <option value="cancelaciones">Cancelaciones</option>
                      <option value="otros">Otros</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent resize-none transition-all"
                    placeholder="Escribe tu mensaje aquí..."
                    required
                  ></textarea>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2AA8B0] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#1E3A5F] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Enviando...
                    </>
                  ) : (
                    'Enviar Mensaje'
                  )}
                </button>
              </form>
            </div>

            {/* Información de contacto */}
            <div className="space-y-8">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h4 className="text-xl font-bold text-[#1E3A5F] mb-6">Información de Contacto</h4>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-[#2AA8B0] rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Dirección</p>
                      <p className="text-gray-600">Yerbal 1136 PB (C1406BWA), CABA, Argentina</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-[#2AA8B0] rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Teléfono</p>
                      <a href="tel:+541144328675" className="text-[#2AA8B0] hover:underline">
                        (011) 4432 8675
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-[#2AA8B0] rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Email</p>
                      <a href="mailto:consultas@nocheydiaturismo.com" className="text-[#2AA8B0] hover:underline break-all">
                        consultas@nocheydiaturismo.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mapa */}
              <div className="bg-white rounded-lg shadow-lg p-4">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3284.016851469891!2d-58.39735842336096!3d-34.59837805705063!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95bcca8c5b3c8b9d%3A0x4dc7e7f8b7a8b5c6!2sYerbal%201136%2C%20C1406BWA%20CABA!5e0!3m2!1sen!2sar!4v1647890123456!5m2!1sen!2sar"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="rounded-lg"
                  title="Ubicación Noche y Día Turismo"
                ></iframe>
              </div>

              {/* Horarios */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h4 className="text-lg font-bold text-[#1E3A5F] mb-4">Horarios de atención</h4>
                <div className="space-y-2 text-gray-600">
                  <p className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-[#2AA8B0]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    <strong>Lunes a viernes:</strong>&nbsp;10 a 13 hs / 14 a 18 hs
                  </p>
                  <p className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-[#2AA8B0]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    <strong>Sábados:</strong>&nbsp;10 a 13 hs
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}

export default Contacto;